/**
 * This is the client-side entrypoint for your tRPC API. It is used to create the `api` object which
 * contains the Next.js App-wrapper, as well as your type-safe React Query hooks.
 *
 * We also create a few inference helpers for input and output types.
 *
 * Link order starts with `createClerkAuthRecoveryLink()` from `~/services/clerk-auth-recovery.service`
 * (shared by `api` and `proxyApi`; see module comment there).
 */

import { createTRPCClient, httpBatchLink, loggerLink, splitLink, TRPCLink } from "@trpc/client";
import { createTRPCNext } from "@trpc/next";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { observable, tap } from "@trpc/server/observable";
import * as process from "process";

import { type AppRouter } from "~/server/api/root";
import { createClerkAuthRecoveryLink } from "~/services/clerk-auth-recovery.service";
import { appConstants } from "~/shared/constants/app.constants";
import { showNotification } from "~/ui";
import superjson from "~/utils/superjson";

const routeOrgSlugHeaderName = "x-shelfcycle-route-org-slug";

type TrpcRequestHeaders = Record<string, string | string[] | undefined>;

export const isPricingEngineListOp = (op: { path: string }): boolean => op.path === "pricingEngine.getEngine";

export const isTagsListForEntitiesOp = (op: { path: string }): boolean => op.path === "tags.listForEntities";

const getBrowserRouteHeaders = (): TrpcRequestHeaders => {
  if (typeof window === "undefined") {
    return {};
  }

  const routePrefix = window.location.pathname.split("/")[1];
  if (!routePrefix?.startsWith("org-")) {
    return {};
  }

  return { [routeOrgSlugHeaderName]: routePrefix.slice(4) };
};

const getBaseUrl = () => {
  if (typeof window !== "undefined") {
    return "";
  } // browser should use relative url
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  } // SSR should use vercel url
  return `http://localhost:${process.env.PORT ?? 3000}`; // dev SSR should use localhost
};

const clerkAuthRecoveryLink = createClerkAuthRecoveryLink();

const errorToastLink: TRPCLink<AppRouter> = () => {
  return ({ next, op }) => {
    return observable(observer => {
      next(op)
        .pipe(
          tap({
            error(err) {
              // FORBIDDEN is intentionally silent: callers gate UI with RBAC helpers and show
              // route-level denied states. Only unexpected INTERNAL_SERVER_ERROR surfaces a toast.
              if (err?.data?.code === "INTERNAL_SERVER_ERROR") {
                showNotification({
                  id: err.message, // use message as the error id to prevent duplicates on retries
                  title: "Something went wrong",
                  message: process.env.NODE_ENV !== "production" ? err.message : "Unexpected Error",
                  color: "red",
                  autoClose: 10000,
                });
              }
            },
          }),
        )
        .subscribe(observer);
    });
  };
};

export const createTrpcBatchSplitLink = (headers: () => TrpcRequestHeaders): TRPCLink<AppRouter> => {
  const batchOptions = {
    transformer: superjson,
    url: `${getBaseUrl()}/api/trpc`,
    headers,
    maxURLLength: appConstants.api.maxBatchUrlLength,
  };
  return splitLink<AppRouter>({
    condition: isPricingEngineListOp,
    true: httpBatchLink<AppRouter>(batchOptions),
    false: splitLink<AppRouter>({
      condition: isTagsListForEntitiesOp,
      true: httpBatchLink<AppRouter>({
        ...batchOptions,
        // One bounded tag chunk per HTTP request so optional tag reads cannot share an envelope with essential reads.
        maxItems: 1,
      }),
      false: httpBatchLink<AppRouter>(batchOptions),
    }),
  });
};

const createTrpcLinks = (options: {
  headers: () => TrpcRequestHeaders;
  loggerEnabled: (opts: { direction: "down" | "up"; result?: unknown }) => boolean;
}): TRPCLink<AppRouter>[] => [
  clerkAuthRecoveryLink,
  errorToastLink,
  loggerLink({ enabled: options.loggerEnabled }),
  createTrpcBatchSplitLink(options.headers),
];

/** A set of type-safe react-query hooks for your tRPC API. */
export const api = createTRPCNext<AppRouter>({
  transformer: superjson,
  config(opts) {
    return {
      /**
       * Links used to determine request flow from client to server.
       *
       * @see https://trpc.io/docs/links
       */
      links: createTrpcLinks({
        headers: typeof window !== "undefined" ? getBrowserRouteHeaders : () => ({ ...opts.ctx?.req?.headers, "x-ssr": "1" }),
        loggerEnabled: opts =>
          (typeof window !== "undefined" && process.env.NODE_ENV === "development") ||
          (opts.direction === "down" && opts.result instanceof Error),
      }),

      queryClientConfig: {
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
          },
        },
      },
    };
  },
  /**
   * Whether tRPC should await queries when server rendering pages.
   *
   * @see https://trpc.io/docs/nextjs#ssr-boolean-default-false
   */
  ssr: false,
});

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;

export const proxyApi = createTRPCClient<AppRouter>({
  links: createTrpcLinks({
    headers: getBrowserRouteHeaders,
    loggerEnabled: opts =>
      (typeof window !== "undefined" && process.env.NODE_ENV === "development") ||
      (opts.direction === "down" && opts.result instanceof Error),
  }),
});
